import { html } from "d3";
import { Loader, Upload, X } from "lucide-react";
import React, { useState, useEffect, useRef } from "react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import Papa from "papaparse";
import { set } from "date-fns";
import Toast from "./Toast";



const domainSuggestions = [
    "gmail.com",
    "yahoo.com",
    "outlook.com",
    "hotmail.com",
    "protonmail.com",
    "icloud.com",
];
const EmailInput = ({ label, emails, setEmails }) => {
    const [input, setInput] = useState("");
    const [filteredSuggestions, setFilteredSuggestions] = useState([]);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const [totalEmails, setTotalEmails] = useState(5);
    const [moreButtonVisible, setMoreButtonVisible] = useState(true);


    const handleAdd = (e) => {
        if (e.key === "ArrowDown") {
            if (filteredSuggestions.length > 0) {
                setHighlightedIndex((prev) =>
                    prev < filteredSuggestions.length - 1 ? prev + 1 : 0
                );
            }
            e.preventDefault();
        } else if (e.key === "ArrowUp") {
            if (filteredSuggestions.length > 0) {
                setHighlightedIndex((prev) =>
                    prev > 0 ? prev - 1 : filteredSuggestions.length - 1
                );
            }
            e.preventDefault();
        } else if (e.key === "Enter") {
            if (
                filteredSuggestions.length > 0 &&
                highlightedIndex >= 0 &&
                highlightedIndex < filteredSuggestions.length
            ) {
                handleSelectSuggestion(filteredSuggestions[highlightedIndex]);
            } else if (input.trim() !== "") {
                if (!emails.includes(input.trim())) {
                    setEmails([...emails, input.trim()]);
                }
                setInput("");
                setFilteredSuggestions([]);
            }
            setHighlightedIndex(-1);
            e.preventDefault();
        }
    };

    const handleRemove = (emailToRemove) => {
        setEmails(emails.filter((email) => email !== emailToRemove));
    };
    const handleChange = (e) => {
        const value = e.target.value;
        setInput(value);

        if (value.includes("@")) {
            const [localPart, domainPart = ""] = value.split("@");

            if (domainPart.length > 0) {
                const filtered = domainSuggestions
                    .filter((d) =>
                        d.toLowerCase().startsWith(domainPart.toLowerCase())
                    )
                    .map((d) => `${localPart}@${d}`);
                setFilteredSuggestions(filtered);
                setHighlightedIndex(filtered.length > 0 ? 0 : -1);
            } else {
                setFilteredSuggestions([]);
                setHighlightedIndex(-1);
            }
        } else {
            setFilteredSuggestions([]);
            setHighlightedIndex(-1);
        }
    };

    const handleSelectSuggestion = (suggestion) => {
        if (!emails.includes(suggestion)) {
            setEmails([...emails, suggestion]);
        }
        setInput("");
        setFilteredSuggestions([]);
        setHighlightedIndex(-1);
    };

    return (
        <div style={{ marginBottom: "1rem", position: "relative" }}>
            <label>{label}:</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.5rem" }}>
                {emails.slice(0, totalEmails).map((email, idx) => (
                    <div
                        key={idx}
                        style={{
                            padding: "0.3rem 0.5rem",
                            backgroundColor: "#e0e0e0",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                        }}
                    >
                        {email}
                        <button
                            style={{
                                marginLeft: "0.3rem",
                                border: "none",
                                background: "transparent",
                                cursor: "pointer",
                            }}
                            onClick={() => handleRemove(email)}
                        >
                            x
                        </button>
                    </div>
                ))}


                {moreButtonVisible && emails.length > 5 && (
                    <button
                        onClick={() => {
                            setTotalEmails(emails.length)
                            setMoreButtonVisible(false);
                        }}
                        style={{
                            padding: "0.3rem 0.5rem",
                            backgroundColor: "#d0d0d0",
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            fontWeight: "bold",
                        }}
                    >
                        +{emails.length - 5} more
                    </button>
                )}

            </div>
            <input
                type="email"
                value={input}
                onChange={handleChange}
                onKeyDown={handleAdd}
                placeholder="Add email and press Enter"
                className="w-full border border-gray-300 rounded-lg mt-2 px-2"
                style={{ flex: "1", minWidth: "150px" }}
            />


            {/* Suggestion dropdown */}
            {filteredSuggestions.length > 0 && (
                <div
                    style={{
                        position: "absolute",
                        background: "#fff",
                        border: "1px solid #ccc",
                        borderRadius: "4px",
                        marginTop: "2px",
                        width: "250px",
                        zIndex: 1000,
                    }}
                >
                    {filteredSuggestions.map((s, idx) => (
                        <div
                            key={idx}
                            onClick={() => handleSelectSuggestion(s)}
                            style={{
                                padding: "0.5rem",
                                cursor: "pointer",
                                borderBottom:
                                    idx !== filteredSuggestions.length - 1
                                        ? "1px solid #eee"
                                        : "none",
                                background: idx === highlightedIndex ? "#f0f0f0" : "#fff",
                            }}
                        >
                            {s}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};



const EmailSendModal = ({
    onClose,


}) => {
    const [toEmails, setToEmails] = useState([]);
    const [ccEmails, setCcEmails] = useState([]);
    const [bccEmails, setBccEmails] = useState([]);
    const [subject, setSubject] = useState("");
    const [description, setDescription] = useState("");
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");
    const [showCc, setShowCc] = useState(false);
    const [showBcc, setShowBcc] = useState(false);
    const fileInputRef = useRef(null);
    const [toast, setToast] = useState({ show: false, message: "", type: "" });



    const handleCSVUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        Papa.parse(file, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                results.data.forEach((row) => {
                    if (row.to && !toEmails.includes(row.to)) {
                        setToEmails((prev) => [...prev, row.to]);
                    }
                    if (row.cc && !ccEmails.includes(row.cc)) {
                        setCcEmails((prev) => [...prev, row.cc]);
                    }
                    if (row.bcc && !bccEmails.includes(row.bcc)) {
                        setBccEmails((prev) => [...prev, row.bcc]);
                    }
                });

            },
        });
    };



    const sendEmail = async () => {
        if (!toEmails.length) {
            setToast({ show: true, message: "To field is required.", type: "error" });
            return;
        }
        if (!subject.trim()) {
            setToast({ show: true, message: "Subject is required.", type: "error" });
            return;
        }
        if (!description || description === "<p><br></p>") {
            setToast({ show: true, message: "Description is required.", type: "error" });
            return;
        }
        setLoading(true);
        setMsg("");

        try {
            const res = await fetch(`${import.meta.env.VITE_API_URL}/api/admin-user/email-marketing`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    to: toEmails,
                    cc: ccEmails,
                    bcc: bccEmails,
                    subject,
                    html: description,

                }),
            });

            const data = await res.json();
            if (res.ok) {
                setMsg(data.message);
                setToast({
                    show: true,
                    message: `Mail sent successfully.`,
                    type: "success"
                });

            } else {
                setMsg(data.message);
                setToast({ show: true, message: "Failed to Send Mail.", type: "error" });

            }
        } catch (error) {
            setMsg(error.message);
            setToast({ show: true, message: "Failed to Send Mail.", type: "error" });

        } finally {
            setLoading(false);
            setTimeout(() => {
                onClose();
            }, 1000);
        }
    };



    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-xl font-bold mb-4">
                        Send Email
                    </h2>
                    <div className="flex gap-2">

                        <button
                            onClick={() => setShowCc(!showCc)}
                            disabled={loading}
                            className="px-4 py-2 bg-primary-600 text-white rounded flex items-center gap-2"
                        >

                            CC
                        </button>
                        <button
                            onClick={() => setShowBcc(!showBcc)}
                            disabled={loading}
                            className="px-4 py-2 bg-primary-600 text-white rounded flex items-center gap-2"
                        >

                            BCC
                        </button>
                    </div>
                </div>
                {/* CSV Upload */}
                {!fileInputRef.current?.files?.length && (
                    <div className="mb-4">
                        <label className="block font-medium mb-1">Upload CSV</label>

                        <input
                            type="file"
                            accept=".csv"
                            onChange={handleCSVUpload}
                            ref={fileInputRef}
                            className="hidden"
                        />

                        <button
                            type="button"
                            onClick={() => fileInputRef.current.click()}
                            className="bg-blue-500 hover:bg-blue-600 text-white font-medium py-2 px-4 rounded-lg"
                        >
                            Choose CSV
                        </button>

                        <p className="text-xs text-gray-500 mt-1">
                            CSV columns: <b>to</b>, <b>cc</b>, <b>bcc</b>
                        </p>
                    </div>
                )}



                {/* Title */}
                {/* <label className="font-medium mb-1">To</label> */}


                <EmailInput label="To" emails={toEmails} setEmails={setToEmails} />
                {showCc && <EmailInput label="Cc" emails={ccEmails} setEmails={setCcEmails} />}
                {showBcc && <EmailInput label="Bcc" emails={bccEmails} setEmails={setBccEmails} />}

                <label>Subject:</label>
                <input
                    type="text"
                    placeholder="Subject"
                    value={subject}
                    onChange={(e) => setSubject(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-2 mb-3"
                    style={{ minWidth: "150px" }}
                />

                {/* Description */}
                <label>Description</label>
                <ReactQuill
                    value={description}
                    onChange={setDescription}
                    className="mb-4 h-32"
                />

                {/* Actions */}
                <div className="flex justify-end space-x-2 mt-[52px]">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-300 rounded"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={sendEmail}
                        disabled={loading}
                        className="px-4 py-2 bg-primary-600 text-white rounded flex items-center gap-2"
                    >
                        {loading && (
                            <Loader className="w-4 h-4 animate-spin" />
                        )}
                        Send
                    </button>
                </div>
            </div>
            {toast.show && (
                <Toast
                    message={toast.message}
                    type={toast.type}
                    onClose={() => setToast({ ...toast, show: false })}
                />
            )}
        </div>
    );
};

export default EmailSendModal;